import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { Control } from '@interfaces/control';
import { DeliverySystem } from '@interfaces/delivery-system';
import { Direction } from '@interfaces/directions';
import { Action, Province } from '@interfaces/province';
import { Configuration } from '@interfaces/rest/interfaces/configuration';
import { Contract, ContractPreviewResponse, ContractValidationResponse, CreateContractDto, ProvinceContractValidationResult, ProvinceSignedContractsResponse, SignContractResponse, UpdateContractDto } from '@interfaces/rest/interfaces/contract';
import { CreateDocumentationDto, Documentation, EditDocumentationDto } from '@interfaces/rest/interfaces/documentations';
import { CreateDriverDto, EditDriverDto } from '@interfaces/rest/interfaces/drivers';
import { CreateIncidentDto, EditIncidentDto, GetIncidentDto, Incident } from '@interfaces/rest/interfaces/incidents';
import { AddressDetail, ChangeFromAddress, ChangeToAddress, EditJourneyDto, FindJourneyDto, GetJourneyDto, Journey, Location } from '@interfaces/rest/interfaces/journey';
import { ActionType, EditLicenseDto, License, OwnershipLicenseDto, WheelActivity } from '@interfaces/rest/interfaces/licenses';
import { CreateManagerDto, EditManagerDto } from '@interfaces/rest/interfaces/manager';
import { Message } from '@interfaces/rest/interfaces/message';
import { CreateNotificationDto, EditNotificationDto } from '@interfaces/rest/interfaces/notifications';
import { CreateOrganizationStampDto, OrganizationStamp, UpdateOrganizationStampDto } from '@interfaces/rest/interfaces/organization-stamp';
import { StatsParams } from '@interfaces/rest/interfaces/others';
import { PaginationParams } from '@interfaces/rest/interfaces/paginationParams';
import { CreateProvinceDto, EditProvinceDto } from '@interfaces/rest/interfaces/province';
import { CreateSessionDto, Session } from '@interfaces/rest/interfaces/sessions';
import { Invoice, InvoicesResponse } from '@interfaces/rest/interfaces/invoice';
import { CreateStripeOperationDto, ProcessPendingPaymentsResponse, StripeOperation, SubscriptionOperationsResponse, UpdateStripeOperationDto } from '@interfaces/rest/interfaces/stripe-operation';
import { DeleteUserQuery, EditUserDto, SignupDto, SignupSocialDto } from '@interfaces/rest/interfaces/user';
import { CreateZoneDto, Zone } from '@interfaces/rest/interfaces/zone';
import { CreateGlobalZoneDto, EditGlobalZoneDto, GlobalZone } from '@interfaces/rest/interfaces/global-zone';
import { User } from '@interfaces/user';
import { NavController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { DpRestService, DpStorageService, DpUtilsService } from 'digitup-lib/dist/digitup-lib';
import { firstValueFrom } from 'rxjs';

export interface GeneralResponse<Type> {
  ok: boolean;
  response: Type;
}

export interface GeneralResponseData<Type> {
  data: {
    result: Type,
    totalElementsByCurrentFilter?: number,
    totalPages?: number
  }
  total: number;
}

export interface GeneralResponseDataTotal<Type> {
  ok: boolean;
  response: {
    data: Type,
    total: number
  }
}

@Injectable({
  providedIn: 'root'
})
export class RestService {
  apiMainUrl = environment.apiMainUrl;
  apiCalculatorUrl = environment.apiCalculatorUrl;

  constructor(
    private rest: DpRestService,
    private storage: DpStorageService,
    private utils: DpUtilsService,
    private nav: NavController,
    private translate: TranslateService,
    private http: HttpClient
  ) { }

  // Health
  async getHealthAll(): Promise<GeneralResponse<any>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/health`);
  }

  // Users
  async signup(data: SignupDto): Promise<GeneralResponse<User>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/users/auth/signup`, data, true);
  }

  async signupSocial(data: SignupSocialDto): Promise<GeneralResponse<User>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/users/auth/signup/social`, data, true);
  }

  async signupOperator(data: SignupDto): Promise<GeneralResponse<User>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/users/auth/signup/operator`, data);
  }

  async getUsersAll(params?: PaginationParams): Promise<GeneralResponse<GeneralResponseData<User[]>>> {
    if (params && !params.sort_by) {
      params.sort_by = 'id:desc';
    }
    return await this.rest.generalGet(`${this.apiMainUrl}/users`, params, false);
  }

  async getBlockedDrivers() {
    return await this.rest.generalGet(`${this.apiMainUrl}/sessions/blocked-drivers`);
  }

  async deleteUser(data: DeleteUserQuery): Promise<GeneralResponse<User>> {
    return await this.rest.generalDelete(`${this.apiMainUrl}/users`, data)
  }

  async getCurrentUser(force = false): Promise<User | null> {
    const user = await this.storage.getItem('taxi_backoffice_current_user');
    if (user && !force) {
      return user;
    } else {
      const apiUser = await this.rest.generalGet(`${this.apiMainUrl}/users/currentUser`);
      if (apiUser.ok && apiUser.response) {
        if (['admin', 'manager', 'operator'].some(role => apiUser.response.role?.includes(role))) {
          await this.storage.setItem('taxi_backoffice_current_user', apiUser.response);
          return apiUser.response;
        } else {
          await this.utils.showToast({ message: this.translate.instant('auth.error.user_role_error'), cssClass: 'danger' });
          this.nav.navigateRoot('/login');
          return null;
        }

      } else {
        return null;
      }
    }
  }

  async getUserRecoveryPasswordLink(email: string): Promise<string> {
    return await this.rest.generalGet(`${this.apiMainUrl}/users/recovery-link/${email}`, {}, false)
  }

  async getUser(id: number): Promise<GeneralResponse<User>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/users/by-id/`, { id });
  }

  async setConfirmEmailLink(email: string): Promise<GeneralResponse<null>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/users/confirm-link`, { email })
  }

  async forgotPassword(email: string): Promise<GeneralResponse<null>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/users/auth/forgot-password`, { email })
  }

  async restoreUser(id: number): Promise<GeneralResponse<null>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/users/restore`, {}, { id })
  }

  async disableUser(id: number): Promise<GeneralResponse<User>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/users/disable`, {}, { id })
  }

  async unlockDriver(driverId: string): Promise<GeneralResponse<null>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/sessions/unlock-driver`, { driverId })
  }

  async editUser(id: number, data: EditUserDto): Promise<GeneralResponse<null>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/users/edit`, data, { id })
  }

  async setUserManager(id: number) {
    return await this.rest.generalPatch(`${this.apiMainUrl}/users/set-manager`, {}, { id })
  }

  async registerUser(data: any) {
    return await this.rest.generalPost(`${this.apiMainUrl}/users`, data, true);
  }

  async setClientHotel(userId: number): Promise<GeneralResponse<null>> {
    return await this.rest.generalPut(`${this.apiMainUrl}/users/set-client-hotel/?userId=${userId}`,);
  }

  async unsetClientHotel(userId: number): Promise<GeneralResponse<User>> {
    return await this.rest.generalPut(`${this.apiMainUrl}/users/unset-client-hotel/?userId=${userId}`,);
  }

  // Managers
  async createManager(data: CreateManagerDto): Promise<GeneralResponse<User>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/managers`, data);
  }

  async getManagersAll(): Promise<GeneralResponse<null>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/managers`);
  }

  async registerManager(token: string, params: { password: string }) {
    return await this.rest.generalPost(`${this.apiMainUrl}/managers/${token}/register`, params, true);
  }

  async editManager(id: number, data: EditManagerDto) {
    return await this.rest.generalPut(`${this.apiMainUrl}/managers/${id}`, data);
  }

  async deleteManager(id: number): Promise<GeneralResponse<null>> {
    return await this.rest.generalDelete(`${this.apiMainUrl}/managers/${id}`);
  }

  async getManagerById(id: number): Promise<GeneralResponse<null>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/managers/${id}`);
  }

  async disableManager(id: number): Promise<GeneralResponse<null>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/managers/${id}/disable`);
  }

  async enableManager(id: number): Promise<GeneralResponse<null>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/managers/${id}/enable`);
  }

  async setProvinceToManger(managerId: number, provinceId: number) {
    return await this.rest.generalPut(`${this.apiMainUrl}/managers/${managerId}/${provinceId}`);
  }

  // Province
  async createProvince(data: CreateProvinceDto): Promise<GeneralResponse<Province>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/province`, data);
  }

  async getProvincesAll(): Promise<GeneralResponse<Province[]>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/province`);
  }

  async getActions(skip?: number, take?: number, provinceId?: number): Promise<GeneralResponse<Action[]>> {
    let queryParams = '';
    const params = [];

    if (skip !== undefined) {
      params.push(`skip=${skip}`);
    }
    if (take !== undefined) {
      params.push(`take=${take}`);
    }
    if (provinceId !== undefined) {
      params.push(`provinceId=${provinceId}`);
    }

    if (params.length > 0) {
      queryParams = '?' + params.join('&');
    }

    return await this.rest.generalGet(`${this.apiMainUrl}/province/actions${queryParams}`);
  }

  async editProvince(id: string, data: EditProvinceDto): Promise<GeneralResponse<Province>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/province/${id}`, data);
  }

  async deleteProvince(id: number): Promise<GeneralResponse<null>> {
    return await this.rest.generalDelete(`${this.apiMainUrl}/province/${id}`);
  }

  async getProvinceById(id: number | string): Promise<GeneralResponse<Province>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/province/${id}`);
  }

  async getManagersByProvinceId(id: number) {
    return await this.rest.generalGet(`${this.apiMainUrl}/province/${id}/managers`);
  }

  async getLicensesByProvinceId(id: number): Promise<GeneralResponse<License[]>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/province/${id}/licenses`);
  }

  async getDriversByProvinceId(id: number) {
    return await this.rest.generalGet(`${this.apiMainUrl}/province/${id}/drivers`);
  }

  // Drivers
  async createDriver(data: CreateDriverDto): Promise<GeneralResponse<User>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/drivers`, data);
  }

  async getDriversAll(params?: PaginationParams): Promise<GeneralResponse<User[]>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/drivers/province`, params);
  }

  async registerDriver(token: string, params: { password: string }) {
    return await this.rest.generalPost(`${this.apiMainUrl}/drivers/${token}/register`, params, true);
  }

  async editDriver(id: number, data: EditDriverDto): Promise<GeneralResponse<null>> {
    return await this.rest.generalPut(`${this.apiMainUrl}/drivers/${id}`, data);
  }

  async deleteDriver(id: number): Promise<GeneralResponse<null>> {
    return await this.rest.generalDelete(`${this.apiMainUrl}/drivers/${id}`);
  }

  async deleteDriverFromLicense(driverId: number, licenseId: number) {
    return await this.rest.generalDelete(`${this.apiMainUrl}/drivers/${driverId}/license/${licenseId}`);
  }

  async getDriversByClientId(id: number): Promise<GeneralResponse<null>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/drivers/${id}/client`);
  }


  async addDriverToLicense(driverId: number, licenseId: number): Promise<GeneralResponse<null>> {
    return await this.rest.generalPut(`${this.apiMainUrl}/drivers/${driverId}/license/${licenseId}`);
  }

  // Licenses
  async getLicensesAll(params?: PaginationParams): Promise<GeneralResponse<GeneralResponseData<License[]>>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/licenses`, params)
  }

  async createLicense(data: License): Promise<GeneralResponse<License>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/licenses`, data);
  }

  async getLicenseById(id: number): Promise<GeneralResponse<License>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/licenses/${id}`);
  }

  async getActionsByLicense(id: number, params: any): Promise<GeneralResponse<any>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/journey/actions/${id}`, params);
  }
  async getActionsByFilter(params: any): Promise<GeneralResponse<any>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/journey/actions/filter`, params);
  }

  async editLicense(id: number, data: EditLicenseDto): Promise<GeneralResponse<License>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/licenses/${id}`, data);
  }

  async updateContractSignDeadline(id: number, contractSignDeadline: string | null): Promise<GeneralResponse<License>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/licenses/${id}`, { contractSignDeadline });
  }

  async addOwnershipLicense(id: number, data: OwnershipLicenseDto): Promise<GeneralResponse<License>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/licenses/${id}`, data);
  }

  async deleteOwnershipLicense(id: number, data: OwnershipLicenseDto): Promise<GeneralResponse<License>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/licenses/${id}`, data);
  }

  async deleteLicense(id: number): Promise<GeneralResponse<null>> {
    return await this.rest.generalDelete(`${this.apiMainUrl}/licenses/${id}`);
  }

  async cleanCacheLicense(): Promise<GeneralResponse<null>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/licenses/clean-cache`);
  }

  async cancelTotalJourney(journeyId: number, reason: string) {
    const data: any = { journeyId }
    if (reason) {
      data.reason = reason;
    }
    return await this.rest.generalPatch(`${this.apiMainUrl}/journey/driver/cancel-journey/total`, data);
  }

  async getJourneysAll(params?: GetJourneyDto): Promise<GeneralResponse<GeneralResponseData<Journey[]>>> {
    if (params && !params.sort_by) {
      params.sort_by = 'id:desc';
    }
    return await this.rest.generalGet(`${this.apiMainUrl}/journey/backoffice`, params);
  }

  async editJourney(id: number, data: EditJourneyDto): Promise<GeneralResponse<Journey>> {
    return await this.rest.generalPut(`${this.apiMainUrl}/journey/${id}`, data);
  }

  async deleteJourney(id: number) {
    return await this.rest.generalDelete(`${this.apiMainUrl}/journey/${id}`);
  }

  async getJourneyById(id: number): Promise<GeneralResponse<Journey>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/journey/${id}`);
  }

  async getJourneyByUserId(id: number) {
    return await this.rest.generalGet(`${this.apiMainUrl}/journey/user/${id}`);
  }

  async getJourneyByDriverId(id: number) {
    return await this.rest.generalGet(`${this.apiMainUrl}/journey/driver/${id}`);
  }

  async getJourneyByLicenseId(id: number): Promise<GeneralResponse<Journey>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/journey/license/${id}`);
  }

  async findJourney(data: FindJourneyDto) {
    return await this.rest.generalPost(`${this.apiMainUrl}/journey/find`, data);
  }

  async getUserJourneysByNumber(number: string) {
    return await this.rest.generalGet(`${this.apiMainUrl}/journey/user-journey-by-number/${number}`);
  }

  async createRecurrentReserve(data: any) {
    return await this.rest.generalPost(`${this.apiMainUrl}/journey/recurrent-reserves`, data);
  }

  async simulatePrice(data: {
    fromLat: number;
    fromLng: number;
    toLat: number;
    toLng: number;
    date?: string;
  }): Promise<GeneralResponse<any>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/journey/price/simulate`, data);
  }

  // async getJourneyDriversByProvinceId(provinceId?: number): Promise<GeneralResponse<DriverInfo[]>> {
  //   return await this.rest.generalGet(`${this.apiMainUrl}/journey/drivers-info`, { provinceId });
  // }

  // async getJourneyZonesByProvinceId(params: { provinceId: number }): Promise<GeneralResponse<DriverInfo[]>> {
  //   return await this.rest.generalGet(`${this.apiMainUrl}/journey/zones-info`, params);
  // }

  async getZoneInfoByProvinceId(provinceId: number): Promise<GeneralResponse<any>> {
    return await this.rest.generalGet(`${this.apiCalculatorUrl}/sessions/zone-info?provinceId=${provinceId}`);
  }

  async coordsToZip(): Promise<GeneralResponse<any>> {
    return await this.rest.generalPatch(`${this.apiCalculatorUrl}/sessions/coords-zip`);
  }

  async changeFromAddress(data: ChangeFromAddress): Promise<GeneralResponse<Journey>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/journey/change-address`, data);
  }

  async changeToAddress(data: ChangeToAddress): Promise<GeneralResponse<Journey>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/journey/change-address`, data);
  }

  async transferReserve(journeyId: number, newLicenseId?: number) {
    const sendData = { journeyId, newLicenseId, transfer: false };
    return await this.rest.generalPatch(`${this.apiMainUrl}/journey/set-license-journey`, sendData)
  }

  async getStats(params: StatsParams) {
    return await this.rest.generalGet(`${this.apiMainUrl}/stats`, params)
  }

  async getStatsNew(params: StatsParams) {
    return await this.rest.generalGet(`${this.apiMainUrl}/stats/new`, params)
  }

  // Sesions
  async createSession(data: CreateSessionDto): Promise<GeneralResponse<Session>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/sessions`, data);
  }

  async getSessionsAll(params: { limit: number, page: number, sort_by?: string }) {
    if (!params.sort_by) {
      params.sort_by = 'id:desc'
    }
    return await this.rest.generalGet(`${this.apiMainUrl}/sessions`, params)
  }

  // async editSession(id: number, data: EditSessionDto): Promise<GeneralResponse<Session>> {
  //   return await this.rest.generalPatch(`${this.apiMainUrl}/sessions/${id}`, data);
  // }

  async deleteSession(id: number): Promise<GeneralResponse<null>> {
    return await this.rest.generalDelete(`${this.apiMainUrl}/sessions/${id}`);
  }

  async getSessionById(id: number): Promise<GeneralResponse<Session>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/sessions/by-id`, { id, coords: true });
  }

  async closeSessionById(id: number) {
    return await this.rest.generalPatch(`${this.apiMainUrl}/sessions/close/${id}`);
  }

  async getSocketStatus() {
    return await this.rest.generalGet(`${this.apiMainUrl}/sessions/check-socket`);
  }

  // Documents
  async createDocumentation(data: CreateDocumentationDto): Promise<GeneralResponse<Documentation>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/documentations`, data);
  }

  async editDocumentation(data: EditDocumentationDto): Promise<GeneralResponse<Documentation>> {
    return await this.rest.generalPut(`${this.apiMainUrl}/documentations`, data);
  }

  async getDocumentationsAll(): Promise<GeneralResponse<Documentation[]>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/documentations`);
  }

  async deleteDocumentation(id: number): Promise<GeneralResponse<null>> {
    return await this.rest.generalDelete(`${this.apiMainUrl}/documentations/${id}`);
  }

  async getDocumentationById(id: number): Promise<GeneralResponse<Documentation>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/documentations/${id}`);
  }

  // Contracts
  async getContractsAll(status?: 'draft' | 'active' | 'inactive'): Promise<GeneralResponse<Contract[]>> {
    const url = status ? `${this.apiMainUrl}/contract?status=${status}` : `${this.apiMainUrl}/contract`;
    return await this.rest.generalGet(url);
  }

  async getContractById(id: number): Promise<GeneralResponse<Contract>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/contract/${id}`);
  }

  async createContract(data: CreateContractDto): Promise<GeneralResponse<Contract>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/contract`, data);
  }

  async updateContract(id: number, data: UpdateContractDto): Promise<GeneralResponse<Contract>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/contract/${id}`, data);
  }

  async activateContract(id: number): Promise<GeneralResponse<Contract>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/contract/${id}/activate`);
  }

  async deleteContract(id: number): Promise<GeneralResponse<null>> {
    return await this.rest.generalDelete(`${this.apiMainUrl}/contract/${id}`);
  }

  async duplicateContract(id: number): Promise<GeneralResponse<Contract>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/contract/${id}/duplicate`, {});
  }

  async updateProvinceContractTypes(provinceId: number, contractTypes: string[]): Promise<GeneralResponse<Province>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/province/${provinceId}/contract-type-driver`, {
      contractTypes
    });
  }

  async updateProvinceContractTypesForProvince(provinceId: number, contractTypes: string[]): Promise<GeneralResponse<Province>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/province/${provinceId}/contract-type`, {
      contractTypes
    });
  }

  async validateContracts(target: 'drivers'): Promise<GeneralResponse<ContractValidationResponse>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/contract/validate?target=${target}`, {});
  }

  async previewContract(data: CreateContractDto): Promise<GeneralResponse<ContractPreviewResponse>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/contract/preview`, data);
  }

  // Province Contract Methods
  async validateProvinceContracts(provinceId: number): Promise<GeneralResponse<ProvinceContractValidationResult>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/contract/province/${provinceId}/validate`);
  }

  async getProvincePendingContracts(provinceId: number): Promise<GeneralResponse<Contract[]>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/contract/province/${provinceId}/pending-signature`);
  }

  async signProvinceContract(contractId: number, signature: string, provinceId: number): Promise<GeneralResponse<SignContractResponse>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/contract/${contractId}/sign`, {
      contractId,
      signature,
      provinceId
    });
  }

  async signLicenseContract(contractId: number, signature: string, licenseId: number): Promise<GeneralResponse<SignContractResponse>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/contract/${contractId}/sign`, {
      contractId,
      signature,
      licenseId
    });
  }

  async getProvinceSignedContracts(provinceId: number): Promise<GeneralResponse<ProvinceSignedContractsResponse>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/contract/province/${provinceId}/signatures`);
  }

  // Payments / Stripe Operations
  async getPaymentsAll(): Promise<GeneralResponse<StripeOperation[]>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/payments`);
  }

  async getPaymentById(id: number): Promise<GeneralResponse<StripeOperation>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/payments/${id}`);
  }

  async createPayment(data: CreateStripeOperationDto): Promise<GeneralResponse<StripeOperation>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/payments`, data);
  }

  async updatePayment(id: number, data: UpdateStripeOperationDto): Promise<GeneralResponse<StripeOperation>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/payments/${id}`, data);
  }

  async deletePayment(id: number): Promise<GeneralResponse<{ message: string }>> {
    return await this.rest.generalDelete(`${this.apiMainUrl}/payments/${id}`);
  }

  async previewSubscriptionOperations(): Promise<GeneralResponse<SubscriptionOperationsResponse>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/payments/subscription-operations/preview`);
  }

  async createSubscriptionOperations(): Promise<GeneralResponse<SubscriptionOperationsResponse>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/payments/subscription-operations`, {});
  }

  async processPendingPayments(): Promise<GeneralResponse<ProcessPendingPaymentsResponse>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/payments/process-pending`, {});
  }

  async getActiveSubscribers(): Promise<GeneralResponse<License[]>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/payments/active-subscribers`);
  }

  async bulkSubscriptionDefault(data: { startDate: string; amount: number }): Promise<GeneralResponse<{ updated: number }>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/payments/bulk-subscription-default`, data);
  }

  async retryPayment(id: number): Promise<GeneralResponse<StripeOperation>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/payments/${id}/retry`, {});
  }

  // Invoices
  async getInvoices(page: number, limit: number): Promise<GeneralResponse<InvoicesResponse>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/invoices`, { page, limit });
  }

  // Organization Stamps
  // All endpoints return GeneralResponse format (same as other endpoints in this service)
  // GET /organization-stamps: { ok: true, response: OrganizationStamp[] }
  // GET /organization-stamps/:id: { ok: true, response: OrganizationStamp }
  async getOrganizationStampsAll(): Promise<GeneralResponse<OrganizationStamp[]>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/organization-stamps`);
  }

  async getOrganizationStampById(id: number): Promise<GeneralResponse<OrganizationStamp>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/organization-stamps/${id}`);
  }

  // Organization Stamps - Create
  // Backend returns: GeneralResponse<OrganizationStamp> format (same as other endpoints)
  // Expected format: { ok: true, response: { id, imagePath, description, enabled, createdAt, updatedAt } }
  // Success: HTTP 200/201 with GeneralResponse<OrganizationStamp>
  // Error: HTTP 4xx/5xx with error message in error.error.message or error.error
  async createOrganizationStamp(data: CreateOrganizationStampDto): Promise<GeneralResponse<OrganizationStamp>> {
    const formData = new FormData();
    formData.append('image', data.image);
    if (data.description) {
      formData.append('description', data.description);
    }
    if (data.enabled !== undefined) {
      formData.append('enabled', data.enabled.toString());
    }

    // Use HttpClient directly - interceptor will add auth token automatically
    // Don't set Content-Type header, let browser set it automatically for FormData
    try {
      const response = await firstValueFrom(
        this.http.post<GeneralResponse<OrganizationStamp>>(
          `${this.apiMainUrl}/organization-stamps`,
          formData
        )
      );
      return response;
    } catch (error: any) {
      console.error('Error creating organization stamp - Full error:', error);
      console.error('Error status:', error?.status);
      console.error('Error error:', error?.error);
      const errorMessage = error?.error?.message ||
        error?.error?.error ||
        error?.message ||
        error?.error ||
        'Unknown error';
      return {
        ok: false,
        response: {
          error: errorMessage,
          status: error?.status,
          statusText: error?.statusText
        } as any
      };
    }
  }

  // PATCH /organization-stamps/:id: { ok: true, response: OrganizationStamp }
  async updateOrganizationStamp(id: number, data: UpdateOrganizationStampDto): Promise<GeneralResponse<OrganizationStamp>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/organization-stamps/${id}`, data);
  }

  // DELETE /organization-stamps/:id: { ok: true, response: null }
  async deleteOrganizationStamp(id: number): Promise<GeneralResponse<null>> {
    return await this.rest.generalDelete(`${this.apiMainUrl}/organization-stamps/${id}`);
  }

  // Directions
  async getDirectionsAll(): Promise<GeneralResponse<{ data: Direction[], total: number }>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/directions`);
  }

  async getDirectionById(id: number): Promise<GeneralResponse<Direction>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/directions/${id}`);
  }

  async createDirection(data: Partial<Direction>): Promise<GeneralResponse<Direction>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/directions`, data);
  }

  async editDirection(id: number, data: Partial<Direction>): Promise<GeneralResponse<Direction>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/directions/${id}`, data);
  }

  async deleteDirection(id: number): Promise<GeneralResponse<null>> {
    return await this.rest.generalDelete(`${this.apiMainUrl}/directions/${id}`);
  }

  async importDirectionsJson(file: File): Promise<GeneralResponse<{ imported: number; errors: number }>> {
    const formData = new FormData();
    formData.append('file', file);

    // Use HttpClient directly - interceptor will add auth token automatically
    // Don't set Content-Type header, let browser set it automatically for FormData
    try {
      const response = await firstValueFrom(
        this.http.post<GeneralResponse<{ imported: number; errors: number }>>(
          `${this.apiMainUrl}/directions/import`,
          formData
        )
      );
      return response;
    } catch (error: any) {
      return {
        ok: false,
        response: { imported: 0, errors: 0 }
      };
    }
  }

  async searchDirections(query: string, limit: number = 10): Promise<GeneralResponse<Direction[]>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/directions/search?query=${encodeURIComponent(query)}&limit=${limit}`);
  }

  // Incidents
  async createIncident(data: CreateIncidentDto): Promise<GeneralResponse<Incident>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/incidents`, data, true)
  }

  async getIncidentsByUserId(params: GetIncidentDto): Promise<GeneralResponse<GeneralResponseData<Incident[]>>> {
    if (!params.sort_by) {
      params['sort_by'] = 'id:desc';
    }
    return await this.rest.generalGet(`${this.apiMainUrl}/incidents`, params);
  }

  async getIncidentById(id: number): Promise<GeneralResponse<Incident>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/incidents/${id}`);
  }

  async editIncidentById(id: number, data: EditIncidentDto): Promise<GeneralResponse<Incident>> {
    return await this.rest.generalPut(`${this.apiMainUrl}/incidents/${id}`, data);
  }

  async deleteIncidentById(id: number) {
    return await this.rest.generalDelete(`${this.apiMainUrl}/incidents/${id}`);
  }

  // Notifications
  async createNotification(data: CreateNotificationDto): Promise<GeneralResponse<Notification>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/notifications`, data);
  }

  async getNotificationById(id: number): Promise<GeneralResponse<Notification>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/notifications/${id}`);
  }

  async editNotification(id: number, data: EditNotificationDto): Promise<GeneralResponse<Notification>> {
    return await this.rest.generalPut(`${this.apiMainUrl}/notifications/${id}`, data);
  }

  async deleteNotification(id: number): Promise<GeneralResponse<Notification>> {
    return await this.rest.generalPut(`${this.apiMainUrl}/notifications/${id}`);
  }

  async getNotificationsByUserId(id: number): Promise<GeneralResponse<Notification[]>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/notifications/user/${id}`);
  }

  async testNotification(): Promise<GeneralResponse<any>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/notifications/test`, {});
  }

  //ZONES

  async updateZones() {
    return await this.rest.generalGet(`${this.apiCalculatorUrl}/zone/update-zone-cache`);
  }

  async createZone(data: CreateZoneDto): Promise<GeneralResponse<Zone>> {
    return await this.rest.generalPost(`${this.apiCalculatorUrl}/zone`, data);
  }

  async getZoneById(zoneId: number): Promise<GeneralResponse<Zone>> {
    return await this.rest.generalGet(`${this.apiCalculatorUrl}/zone/${zoneId}`);
  }

  async deleteZone(zoneId: string): Promise<GeneralResponse<Zone>> {
    return await this.rest.generalDelete(`${this.apiCalculatorUrl}/zone/${zoneId}`);
  }

  async getZoneByProvinceId(provinceId: number): Promise<GeneralResponse<Zone[]>> {
    const params = { all: true };
    return await this.rest.generalGet(`${this.apiCalculatorUrl}/zone/province/${provinceId}`, params);
  }

  async changeQueuePosition(licenseId: number, toFirst = false) {
    return await this.rest.generalPatch(`${this.apiCalculatorUrl}/zone/change-queue-position`, { licenseId, toFirst });
  }

  async addParadaToZone(zoneId: number, data: CreateZoneDto): Promise<GeneralResponse<Zone>> {
    return await this.rest.generalPatch(`${this.apiCalculatorUrl}/zone/${zoneId}`, data);
  }

  async editZone(zoneId: number, data: CreateZoneDto): Promise<GeneralResponse<Zone>> {
    return await this.rest.generalPatch(`${this.apiCalculatorUrl}/zone/${zoneId}`, data);
  }

  async deleteParada(paradaId: string): Promise<GeneralResponse<any>> {
    return await this.rest.generalDelete(`${this.apiCalculatorUrl}/zone/parada/${paradaId}`);
  }

  //GLOBAL ZONES

  async getGlobalZones(): Promise<GeneralResponse<GlobalZone[]>> {
    return await this.rest.generalGet(`${this.apiCalculatorUrl}/global-zone`);
  }

  async getGlobalZoneById(id: string): Promise<GeneralResponse<GlobalZone>> {
    return await this.rest.generalGet(`${this.apiCalculatorUrl}/global-zone/${id}`);
  }

  async createGlobalZone(data: CreateGlobalZoneDto): Promise<GeneralResponse<GlobalZone>> {
    return await this.rest.generalPost(`${this.apiCalculatorUrl}/global-zone`, data);
  }

  async editGlobalZone(id: string, data: EditGlobalZoneDto): Promise<GeneralResponse<GlobalZone>> {
    return await this.rest.generalPatch(`${this.apiCalculatorUrl}/global-zone/${id}`, data);
  }

  async deleteGlobalZone(id: string): Promise<GeneralResponse<GlobalZone>> {
    return await this.rest.generalDelete(`${this.apiCalculatorUrl}/global-zone/${id}`);
  }

  //MESSAGES

  async sendRecoverData(data: any) {
    return await this.rest.generalPost(`${this.apiMainUrl}/users/recover-data`, data);
  }

  async getMessagesByUserId(params: { userId?: number, page?: number, limit?: number }) {
    return await this.rest.generalGet(`${this.apiMainUrl}/messages`, params);
  }

  async getAllMessages() {
    return await this.rest.generalGet(`${this.apiMainUrl}/messages/chats/current-user`);
  }

  async sendMessagesAllDrivers(params: { text: string, hiddenMessage: boolean, all: boolean }) {
    return await this.rest.generalPost(`${this.apiMainUrl}/messages/all-drivers`, params);
  }

  async sendMessagesByLicenseId(licenseId: number, params: { text: string, hiddenMessage: boolean }) {
    return await this.rest.generalPost(`${this.apiMainUrl}/messages/license/${licenseId}`, params);
  }

  async createMessage(params: { to: number, text: string, hiddenMessage: boolean }) {
    return await this.rest.generalPost(`${this.apiMainUrl}/messages`, params);
  }

  async deleteMessage(messageId: string): Promise<GeneralResponse<Message>> {
    return await this.rest.generalDelete(`${this.apiCalculatorUrl}/messages/${messageId}`);
  }

  async deleteChatWithUser(userId: number): Promise<GeneralResponse<Message>> {
    return await this.rest.generalDelete(`${this.apiMainUrl}/messages/chat-with-user?userId=${userId}`);
  }

  async sendUserAction(userAction: any): Promise<GeneralResponse<any>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/users/action`, userAction);
  }


  // CONFIGURATIONS

  async getConfigurations(id: number): Promise<GeneralResponse<Configuration>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/configuration/${id}`);
  }

  async editConfigurations(id: number, data: { status: boolean }): Promise<GeneralResponse<Configuration>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/configuration/${id}`, data);
  }

  // DELIVERY SYSTEM
  async getDeliverySystems(provinceId: number): Promise<GeneralResponse<DeliverySystem[]>> {
    const params = { provinceId };
    return await this.rest.generalGet(`${this.apiMainUrl}/delivery-system`, params);
  }

  async getDeliverySystemById(id: string): Promise<GeneralResponse<DeliverySystem>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/delivery-system/${id}`);
  }

  async updateDeliverySystem(id: string, data: DeliverySystem): Promise<GeneralResponse<any>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/delivery-system/${id}`, data);
  }

  async createDeliverySystem(data: DeliverySystem): Promise<GeneralResponse<DeliverySystem>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/delivery-system`, data);
  }

  async deleteDeliverySystem(id: number): Promise<GeneralResponse<any>> {
    return await this.rest.generalDelete(`${this.apiMainUrl}/delivery-system/${id}`);
  }

  async cloneDeliverySystem(id: number, name: string): Promise<GeneralResponse<DeliverySystem>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/delivery-system/clone/${id}`, { name });
  }

  async resetWheel(id: string): Promise<GeneralResponse<any>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/licenses/reset-wheel-counter/${id}`);
  }

  async sendNewVersion(version: string): Promise<GeneralResponse<any>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/configuration/send-update-apk?version=${version}`);
  }

  async sendNotification(provinces: number[], message: string): Promise<GeneralResponse<Province>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/province/send-notification`, { provinces, message });
  }

  async getWheelActivity(id: number, page = 1, sortParams: any): Promise<GeneralResponse<GeneralResponseData<WheelActivity[]>>> {
    const params = {
      limit: 10,
      page,
      'where.license': id
    }
    const mergedParams = { ...params, ...sortParams };
    return await this.rest.generalGet(`${this.apiMainUrl}/wheelActivity`, mergedParams);
  }

  async updateWheelActivity(id: number, params: { refund: number, refundNote: string }): Promise<GeneralResponse<WheelActivity>> {
    return await this.rest.generalPatch(`${this.apiMainUrl}/wheelActivity/refund/${id}`, params);
  }

  async addWheelActivity(params: { licenseId: number, refund: number, refundNote: string, actionType: ActionType, journeyId?: number }): Promise<GeneralResponse<WheelActivity>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/wheelActivity/add-refund`, params);
  }

  async requestControlData(uid?: number, province?: number) {
    let params = '';
    if (uid) {
      params += `uid=${uid}`;
    }
    if (province) {
      params += `&province=${province}`;
    }
    return await this.rest.generalPost(`${this.apiCalculatorUrl}/control/send-control?${params}`);
  }

  async getControls(): Promise<GeneralResponse<Control[]>> {
    return await this.rest.generalGet(`${this.apiCalculatorUrl}/control`);
  }

  async getSuggestionsGeoapify(text: string, bias: string): Promise<Location[] | null> {
    const apiKey = environment.geoapify;
    const url = `https://api.geoapify.com/v1/geocode/autocomplete?text=${text}&filter=rect:2.259212969172495,39.23653679350352,3.541465270876415,40.00984082021358&bias=rect:${bias}|countrycode:none&format=json&apiKey=${apiKey}`;
    const result = await this.simpleGet(url);
    console.log('getSuggestionsGeoapify', result);

    const locations: Location[] = [];
    if (result?.results?.length) {
      result.results.forEach((elem: any) => {
        locations.push({
          lat: elem.lat,
          lng: elem.lon,
          main: elem.address_line1,
          secondary: elem.address_line2
        });
        // if (elem.rank?.confidence || 0 > 0.7) {
        // }
      });
    }
    return locations;
  }

  async getAddressByLatLng(lat: number, lng: number): Promise<AddressDetail | null> {
    const apiKey = environment.geoapify;
    const url = `https://api.geoapify.com/v1/geocode/reverse?lat=${lat}&lon=${lng}&format=json&apiKey=${apiKey}`;
    const result = await this.simpleGet(url);
    console.log('result?.features?.[0]', result);

    if (result?.results?.[0]?.address_line1 && result?.results?.[0]?.address_line2) {
      return { title: result?.results?.[0]?.address_line1, description: result?.results?.[0]?.address_line2 };
    }
    return null;
  }

  async simpleGet(url: string): Promise<any> {
    try {
      const response = await fetch(url);
      const result = await response.json();
      return result;
    } catch (error) {
      console.log('Error:', error);
      return null;
    }
  }

  /**
   * Envía un mensaje de prueba a WhatsApp o Telegram
   */
  async sendTestMessage(data: {
    platform: 'whatsapp' | 'telegram';
    to: string;
    message: string;
    buttons?: Array<{ text: string; id?: string; callbackData?: string }>;
    requestLocation?: boolean;
    imageUrl?: string;
  }): Promise<GeneralResponse<any>> {
    return await this.rest.generalPost(`${this.apiMainUrl}/whatsapp/test-message`, data);
  }

  // ==================== Storage Test ====================

  async storageUploadPdf(file: File): Promise<GeneralResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await firstValueFrom(
        this.http.post<GeneralResponse<any>>(
          `${this.apiMainUrl}/contract/storage-test/upload`,
          formData
        )
      );
      return response;
    } catch (error: any) {
      console.error('Error uploading PDF:', error);
      return {
        ok: false,
        response: {
          error: error?.error?.message || error?.message || 'Unknown error'
        }
      };
    }
  }

  async storageDeletePdf(fileName: string): Promise<GeneralResponse<any>> {
    return await this.rest.generalDelete(`${this.apiMainUrl}/contract/storage-test/${encodeURIComponent(fileName)}`);
  }

  async storageListPdfs(): Promise<GeneralResponse<any>> {
    return await this.rest.generalGet(`${this.apiMainUrl}/contract/storage-test/list`);
  }
}

